/**
 * The decision behind an MCP `execute_with_plan` command.
 *
 * This lives outside `QueryEditorTab` for one reason: a binding check buried in
 * a 2000-line component is a check no test can hold down. A review deleted the
 * comparison when it was inline and the whole suite — 6491 tests — stayed green.
 * As a function it is driven directly, and deleting the comparison fails
 * `planCommandDispatch.test.ts` immediately.
 *
 * The rule it encodes: an `actual` run executes the statement, so the live
 * buffer must still be text the permission gate classified. `estimated` only
 * compiles the statement and has nothing to bind — which is why the command type
 * gives it no `allowedSources` to ignore.
 */

import {
  agentExecution,
  isSourceAuthorized,
  UNAUTHORIZED_SOURCE_MESSAGE,
} from '../../core/types/executionAuthorization';

export type PlanCommand =
  | { planMode: 'actual'; allowedSources: string[] }
  | { planMode: 'estimated' };

export type PlanCommandDecision =
  /** Capture an estimated plan; the statement is compiled, not run. */
  | { kind: 'explain' }
  /** Run the statement with an actual plan. */
  | { kind: 'execute' }
  /** The buffer is no longer text that was checked. Nothing runs. */
  | { kind: 'refuse'; message: string };

/**
 * Decides what an `execute_with_plan` command may do against the buffer as it
 * stands right now.
 *
 * `liveSql` must be read **before** any formatting pass: formatting rewrites the
 * buffer, so comparing afterwards would compare against text nobody checked.
 */
export const decidePlanCommand = (command: PlanCommand, liveSql: string): PlanCommandDecision => {
  if (command.planMode === 'estimated') return { kind: 'explain' };

  if (!isSourceAuthorized(agentExecution(command.allowedSources), liveSql)) {
    return { kind: 'refuse', message: UNAUTHORIZED_SOURCE_MESSAGE };
  }

  return { kind: 'execute' };
};
